<template>
  <section v-if="hasContent" class="clinic-hub-links">
    <div v-if="okrugs.length" class="clinic-hub-links__block">
      <h2 class="clinic-hub-links__title">
        Клиники по округам
        <span class="clinic-hub-links__count">{{ totalOkrugClinics }}</span>
      </h2>
      <ul class="clinic-hub-links__list">
        <li v-for="item in okrugs" :key="item.slug">
          <NuxtLink :to="item.path" class="clinic-hub-links__link">
            {{ item.label }}
            <span class="clinic-hub-links__item-count">{{ item.count }}</span>
          </NuxtLink>
        </li>
      </ul>
    </div>

    <div v-if="rayons.length" class="clinic-hub-links__block">
      <h2 class="clinic-hub-links__title">
        Клиники по районам
        <span class="clinic-hub-links__count">{{ totalRayonClinics }}</span>
      </h2>
      <ul class="clinic-hub-links__list">
        <li v-for="item in visibleRayons" :key="item.slug">
          <NuxtLink :to="item.path" class="clinic-hub-links__link">
            {{ item.label }}
            <span class="clinic-hub-links__item-count">{{ item.count }}</span>
          </NuxtLink>
        </li>
      </ul>
      <button
        v-if="rayons.length > rayonLimit && !showAllRayons"
        type="button"
        class="clinic-hub-links__more"
        @click="showAllRayons = true"
      >
        Показать все районы
      </button>
    </div>

    <div v-if="metros.length" class="clinic-hub-links__block">
      <h2 class="clinic-hub-links__title">Клиники по станциям метро</h2>
      <ul class="clinic-hub-links__list">
        <li v-for="item in visibleMetros" :key="item.slug">
          <NuxtLink :to="item.path" class="clinic-hub-links__link">
            {{ item.label }}
            <span class="clinic-hub-links__item-count">{{ item.count }}</span>
          </NuxtLink>
        </li>
      </ul>
      <button
        v-if="metros.length > metroLimit && !showAllMetros"
        type="button"
        class="clinic-hub-links__more"
        @click="showAllMetros = true"
      >
        Показать все станции
      </button>
    </div>

    <div v-if="alwaysOpen" class="clinic-hub-links__block">
      <h2 class="clinic-hub-links__title">
        Круглосуточные
        <span class="clinic-hub-links__count">{{ alwaysOpen.count }}</span>
      </h2>
      <ul class="clinic-hub-links__list clinic-hub-links__list--clinics">
        <li v-for="clinic in alwaysOpen.top" :key="clinic.slug">
          <NuxtLink :to="clinic.path" class="clinic-hub-links__link">{{ clinic.name }}</NuxtLink>
        </li>
      </ul>
      <NuxtLink :to="alwaysOpen.path" class="clinic-hub-links__more clinic-hub-links__more--link">
        Показать все круглосуточные
      </NuxtLink>
    </div>
  </section>
</template>

<script lang="ts" setup>
export type ClinicHubLinkItem = {
  slug: string
  label: string
  count: number
  path: string
}

export type ClinicHubsPayload = {
  okrugs: ClinicHubLinkItem[]
  rayons: ClinicHubLinkItem[]
  metros: ClinicHubLinkItem[]
  alwaysOpen: {
    count: number
    path: string
    top: { slug: string; name: string; path: string }[]
  } | null
}

const props = defineProps<{
  hubs: ClinicHubsPayload | null | undefined
}>()

const metroLimit = 50
const rayonLimit = 30
const showAllMetros = ref(false)
const showAllRayons = ref(false)

const okrugs = computed(() => props.hubs?.okrugs ?? [])
const rayons = computed(() => props.hubs?.rayons ?? [])
const metros = computed(() => props.hubs?.metros ?? [])
const alwaysOpen = computed(() => props.hubs?.alwaysOpen ?? null)

const visibleMetros = computed(() =>
  showAllMetros.value ? metros.value : metros.value.slice(0, metroLimit),
)
const visibleRayons = computed(() =>
  showAllRayons.value ? rayons.value : rayons.value.slice(0, rayonLimit),
)

const totalOkrugClinics = computed(() => okrugs.value.reduce((sum, item) => sum + item.count, 0))
const totalRayonClinics = computed(() => rayons.value.reduce((sum, item) => sum + item.count, 0))

const hasContent = computed(() =>
  okrugs.value.length > 0
  || rayons.value.length > 0
  || metros.value.length > 0
  || alwaysOpen.value !== null,
)
</script>

<style lang="scss">
.clinic-hub-links {
  margin: 48px 0 24px;

  &__block {
    margin-bottom: 32px;
  }

  &__title {
    font-size: 24px;
    line-height: 1.3;
    margin: 0 0 16px;
    font-weight: 600;
  }

  &__count {
    color: $color-gray;
    font-weight: 400;
    font-size: 18px;
    margin-left: 8px;
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    list-style: none;
    margin: 0;
    padding: 0;

    // common.scss рисует фиолетовую точку на каждом ul > li
    > li {
      padding-left: 0;
      margin-bottom: 0;

      &::before {
        content: none;
      }
    }

    &--clinics {
      flex-direction: column;
      gap: 8px;
    }
  }

  &__link {
    color: $color-accent-text;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__item-count {
    color: $color-gray;
    margin-left: 4px;
    font-size: 14px;
  }

  &__more {
    margin-top: 12px;
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font: inherit;
    color: $color-accent-text;
    text-decoration: underline;

    &--link {
      display: inline-block;
    }
  }

  @include media(sm) {
    margin-top: 32px;

    &__title {
      font-size: 20px;
    }
  }
}
</style>
